import type { ModifierState } from "@/domain/profile-resolver/index";
import type { Keyboard as KeyboardModel, RenderKeycap } from "@/domain/types";
import { Keyboard } from "@/render/board-compose/Keyboard";
import type { ChipReservation, RenderZone } from "@/render/mouse-map/compose";
import { MouseMap } from "@/render/mouse-map/MouseMap";
import { FIXED_TWEAKS } from "@/render/tweaks/use-tweaks";

/**
 * The Keyboard + MouseMap render pair, shared by the single board and both compare sides (a mouse
 * zone behaves exactly like a keycap). The visual treatment is the fixed Steel styling (FIXED_TWEAKS)
 * now that backlit caps / ability names / glow are no longer user options. `litCodes` is the active
 * flashlight (search/hover off-compare, the diff flashlight on-compare); `dimCategories` isolates a
 * Category group; `dimFallback` dims base-fallback caps while a combo is held.
 */
export function Surfaces({
  board,
  keycaps,
  zones,
  litCodes,
  dimCategories,
  dimFallback,
  unit,
  mods,
  reserve,
  pressedCodes,
  onPress,
  iconFor,
}: {
  board: KeyboardModel;
  keycaps: RenderKeycap[];
  zones: RenderZone[];
  litCodes: string[] | null;
  dimCategories: string[] | null;
  dimFallback: boolean;
  unit: number;
  mods: ModifierState;
  reserve: ChipReservation;
  pressedCodes: string[] | null;
  onPress: (code: string, legend: string) => void;
  /** Resolves a command id to its icon-tile SVG; injected by the page (SC2-aware) so render stays
   * game-agnostic. Undefined for a simple Game. */
  iconFor?: (cmd: string | undefined) => string | null;
}) {
  return (
    <>
      <Keyboard
        board={board}
        keycaps={keycaps}
        unit={unit}
        mods={mods}
        keycapStyle={FIXED_TWEAKS.capStyle}
        keycapLabel={FIXED_TWEAKS.capLabel}
        iconFor={iconFor}
        keyGlow={FIXED_TWEAKS.keyGlow}
        showNames={FIXED_TWEAKS.showNames}
        onPress={onPress}
        dimCategories={dimCategories}
        litCodes={litCodes}
        dimFallback={dimFallback}
        pressedCodes={pressedCodes}
      />
      <MouseMap
        zones={zones}
        unit={unit}
        reserve={reserve}
        keycapStyle={FIXED_TWEAKS.capStyle}
        keyGlow={FIXED_TWEAKS.keyGlow}
        showNames={FIXED_TWEAKS.showNames}
        dimCategories={dimCategories}
        litCodes={litCodes}
        dimFallback={dimFallback}
        pressedCodes={pressedCodes}
      />
    </>
  );
}
